import {
  Button,
  Checkbox,
  Combobox,
  FieldGroup,
  FieldWrap,
  Modal,
  ModalContent,
  ModalFooter,
  Select
} from '@deque/cauldron-react';
import { type ComboboxValue } from '@deque/cauldron-react/lib/components/Combobox/ComboboxOption';
import type { TFunction } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CustomLabelsModal.css';
import { type SaveSelectedLabelsArgs } from './Field/LabelFieldAssignment';
import { getAxeFieldsOptions, type SelectOption } from './FieldMapping';

export interface ComboboxOption {
  label: string;
  value: string;
}

export interface AxeTagOption {
  id: string;
  label: string;
}

export enum AxeIssueTagFieldCategories {
  AccessibilityStandards = 'AccessibilityStandards',
  Categories = 'Categories',
  IssueType = 'IssueType',
  WCAGSuccessCriteria = 'WCAGSuccessCriteria'
}

export const AxeTagsOptionLabelByCategory: Record<
  AxeIssueTagFieldCategories,
  (t: TFunction) => string
> = {
  [AxeIssueTagFieldCategories.AccessibilityStandards]: (t: TFunction) =>
    t('Accessibility standards (WCAG, Section 508, RGAA, etc.)'),
  [AxeIssueTagFieldCategories.Categories]: (t: TFunction) =>
    t('Categories (aria, color contrast, keyboard, etc.)'),
  [AxeIssueTagFieldCategories.IssueType]: (t: TFunction) =>
    t('Issue type (best practice, experimental, etc.)'),
  [AxeIssueTagFieldCategories.WCAGSuccessCriteria]: (t: TFunction) =>
    t('WCAG success criteria (1.2.3, 4.1.2, etc.)')
};

export interface CustomLabelsModalProps {
  isOpen: boolean;
  onSave: (args: SaveSelectedLabelsArgs) => void;
  onClose: () => void;
  axeTagOptions?: SelectOption[];
  availableAxeFields?: SelectOption[];
  initialValues?: {
    jiraLabels: string[];
    axeTags: string[];
    axeField: string;
  };
  integrationProductLabels: string[];
}

type ComboboxSelectionChange = {
  value: ComboboxValue[];
  previousValue: ComboboxValue[];
  target: HTMLElement;
};

export const CustomLabelsModal = ({
  isOpen,
  onSave,
  onClose,
  availableAxeFields,
  initialValues,
  integrationProductLabels
}: CustomLabelsModalProps) => {
  const { t } = useTranslation();

  const [selectedJiraLabels, setSelectedJiraLabels] = useState<string[]>([]);
  const [selectedAxeTags, setSelectedAxeTags] = useState<string[]>([]);
  const [selectedAxeField, setSelectedAxeField] = useState<string>('');

  useEffect(() => {
    setSelectedJiraLabels(initialValues?.jiraLabels ?? []);
    setSelectedAxeTags(initialValues?.axeTags ?? []);
    setSelectedAxeField(initialValues?.axeField ?? '');
  }, [initialValues]);

  const availableIntegrationLabels = useMemo<ComboboxOption[]>(
    () =>
      integrationProductLabels.map(label => ({
        label,
        value: label
      })),
    [integrationProductLabels]
  );

  const computedAxeTagOptions = useMemo<SelectOption[]>(
    () =>
      Object.entries(AxeTagsOptionLabelByCategory).map(
        ([category, optionLabelFunction]) => ({
          key: category,
          value: category,
          label: optionLabelFunction(t)
        })
      ),
    [t]
  );

  const axeFieldOptions = useMemo<SelectOption[]>(
    () => availableAxeFields ?? getAxeFieldsOptions(t),
    [availableAxeFields, t]
  );

  const totalSelectedLabels = useMemo(() => {
    if (typeof selectedJiraLabels === 'number') {
      return selectedJiraLabels;
    }

    return (
      (selectedJiraLabels?.length ?? 0) +
      (selectedAxeTags?.length ?? 0) +
      (selectedAxeField ? 1 : 0)
    );
  }, [
    selectedJiraLabels,
    selectedJiraLabels,
    selectedAxeTags,
    selectedAxeField
  ]);

  const handleClose = () => {
    setSelectedJiraLabels(initialValues?.jiraLabels ?? []);
    setSelectedAxeTags(initialValues?.axeTags ?? []);
    setSelectedAxeField(initialValues?.axeField ?? '');

    onClose();
  };

  const handleJiraLabelSelectionChange = ({
    value
  }: ComboboxSelectionChange) => {
    if (!value) {
      setSelectedJiraLabels([]);
      return;
    }

    if (Array.isArray(value)) {
      setSelectedJiraLabels(value.filter(Boolean) as string[]);
      return;
    }

    setSelectedJiraLabels([value]);
  };

  const toggleAxeTag = (tagId: string) => {
    if (selectedAxeTags.includes(tagId)) {
      setSelectedAxeTags(selectedAxeTags.filter(id => id !== tagId));
    } else {
      setSelectedAxeTags([...selectedAxeTags, tagId]);
    }
  };

  const onSaveButtonClick = () => {
    onSave({ selectedJiraLabels, selectedAxeTags, selectedAxeField });
    onClose();
  };

  return (
    <Modal heading={t('Select labels')} onClose={handleClose} show={isOpen}>
      <ModalContent className={styles.modalContent}>
        <p className={styles.description}>
          {t('Choose which labels to apply to this template')}
        </p>

        <div className={styles.section}>
          <Combobox
            label={t('Jira labels')}
            options={availableIntegrationLabels}
            multiselect
            value={selectedJiraLabels}
            onSelectionChange={handleJiraLabelSelectionChange}
            className={styles.control}
          />
        </div>

        <div className={styles.section}>
          <FieldWrap as={FieldGroup} label={t('Axe tags')}>
            {computedAxeTagOptions.map(option => (
              <Checkbox
                key={option.key}
                id={`axe-tag-${option.key}`}
                label={option.label}
                checked={selectedAxeTags.includes(option.key)}
                onChange={() => toggleAxeTag(option.key)}
              />
            ))}
          </FieldWrap>
        </div>

        <div className={styles.section}>
          <Select
            label={t('Axe fields')}
            value={selectedAxeField}
            onChange={({ target }) => setSelectedAxeField(target.value)}
            className={styles.control}
          >
            {axeFieldOptions.map(option => (
              <option key={option.key || option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <p className={styles.selectionSummary}>
          {t('You have selected {{count}} labels for this template.', {
            count: totalSelectedLabels
          })}
        </p>
      </ModalContent>
      <ModalFooter>
        <Button variant="primary" onClick={onSaveButtonClick}>
          {t('Save selections')}
        </Button>
        <Button variant="secondary" onClick={handleClose}>
          {t('Cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
